import React, { useState, useEffect } from 'react';
import { teamsAPI, matchesAPI, contentAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import { FaFutbol, FaSquare, FaTrophy, FaChartBar, FaShieldAlt, FaMedal } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import PageCover from '../components/PageCover';
import { useLanguage } from '../context/LanguageContext';

const StatisticsPage = () => {
  const { language } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scorers');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsRes, matchesRes, playersRes] = await Promise.all([
        teamsAPI.getAll(),
        matchesAPI.getAll(),
        contentAPI.getPlayers()
      ]);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Générer des statistiques de buteurs réels
  const generateScorers = () => {
    return players
      .filter(p => p.goals > 0)
      .map(p => ({
        id: p.id,
        name: { ar: p.name_ar, fr: p.name },
        team: p.team_name,
        teamLogo: p.logo_url,
        goals: p.goals,
        matches: p.matches_played,
        penalties: 0
      }))
      .sort((a, b) => b.goals - a.goals)
      .slice(0, 20);
  };

  // Générer des statistiques de cartons réels
  const generateCards = (type) => {
    const list = players
      .filter(p => type === 'yellow' ? p.yellow_cards > 0 : p.red_cards > 0)
      .map(p => ({
        id: p.id,
        name: { ar: p.name_ar, fr: p.name },
        team: p.team_name,
        teamLogo: p.logo_url,
        yellowCards: p.yellow_cards,
        redCards: p.red_cards,
        matches: p.matches_played
      }));

    if (type === 'yellow') {
      return list.sort((a, b) => b.yellowCards - a.yellowCards).slice(0, 20);
    } else {
      return list.sort((a, b) => b.redCards - a.redCards).slice(0, 20);
    }
  };

  // Générer statistiques d'équipes
  const generateTeamStats = () => {
    return teams.map(team => {
      const teamMatches = matches.filter(m => 
        m.home_team_id === team.id || m.away_team_id === team.id
      );
      
      const finishedMatches = teamMatches.filter(m => m.status === 'finished');
      
      const goalsScored = finishedMatches.reduce((sum, m) => {
        return sum + (m.home_team_id === team.id ? m.home_score : m.away_score);
      }, 0);
      
      const goalsConceded = finishedMatches.reduce((sum, m) => {
        return sum + (m.home_team_id === team.id ? m.away_score : m.home_score);
      }, 0);

      return {
        id: team.id,
        name: team.name,
        logo: team.logo_url,
        matches: finishedMatches.length,
        goalsScored,
        goalsConceded,
        goalDifference: goalsScored - goalsConceded,
        avgScored: finishedMatches.length > 0 ? (goalsScored / finishedMatches.length).toFixed(2) : 0,
        avgConceded: finishedMatches.length > 0 ? (goalsConceded / finishedMatches.length).toFixed(2) : 0
      };
    }).sort((a, b) => b.goalsScored - a.goalsScored);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const scorers = generateScorers();
  const yellowCards = generateCards('yellow');
  const redCards = generateCards('red');
  const teamStats = generateTeamStats();

  const text = {
    ar: {
      title: 'الإحصائيات',
      subtitle: 'إحصائيات شاملة للبطولة - الهدافين والبطاقات',
      topScorers: 'الهدافون',
      yellowCards: 'البطاقات الصفراء',
      redCards: 'البطاقات الحمراء',
      teamStats: 'إحصائيات الفرق',
      rank: 'الترتيب',
      player: 'اللاعب',
      team: 'الفريق',
      goals: 'أهداف',
      matches: 'مباريات',
      penalties: 'جزاءات',
      yellow: 'صفراء',
      red: 'حمراء',
      goalsScored: 'أهداف مسجلة',
      goalsConceded: 'أهداف متلقاة',
      goalDiff: 'الفارق',
      average: 'المعدل'
    },
    fr: {
      title: 'Statistiques',
      subtitle: 'Statistiques complètes du championnat - Buteurs et Cartons',
      topScorers: 'Meilleurs Buteurs',
      yellowCards: 'Cartons Jaunes',
      redCards: 'Cartons Rouges',
      teamStats: 'Statistiques des Équipes',
      rank: 'Rang',
      player: 'Joueur',
      team: 'Équipe',
      goals: 'Buts',
      matches: 'Matchs',
      penalties: 'Pénaltys',
      yellow: 'Jaunes',
      red: 'Rouges',
      goalsScored: 'Buts marqués',
      goalsConceded: 'Buts encaissés',
      goalDiff: 'Diff',
      average: 'Moyenne'
    }
  };

  const t = text[language];

  return (
    <div style={styles.container}>
      <PageCover title={t.title} subtitle={t.subtitle} icon={FaChartBar} />

      {/* Tabs */}
      <section style={styles.tabsSection}>
        <div style={styles.tabs}>
          <button
            style={{...styles.tab, ...(activeTab === 'scorers' && styles.activeTab)}}
            onClick={() => setActiveTab('scorers')}
          >
            <FaFutbol /> {t.topScorers}
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'yellow' && styles.activeTab)}}
            onClick={() => setActiveTab('yellow')}
          >
            <FaSquare style={{color: '#FFC107'}} /> {t.yellowCards}
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'red' && styles.activeTab)}}
            onClick={() => setActiveTab('red')}
          >
            <FaSquare style={{color: '#f44336'}} /> {t.redCards}
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'teams' && styles.activeTab)}}
            onClick={() => setActiveTab('teams')}
          >
            <FaChartBar /> {t.teamStats}
          </button>
        </div>
      </section>

      {/* Content */}
      <section style={styles.content}>
        {/* Top Scorers */}
        {activeTab === 'scorers' && (
          <div style={styles.statsContainer}>
            <div style={styles.podium}>
              {scorers.slice(0, 3).map((player, index) => (
                <div 
                  key={player.id}
                  style={{
                    ...styles.podiumItem,
                    order: index === 0 ? 2 : index === 1 ? 1 : 3,
                    height: index === 0 ? '200px' : index === 1 ? '160px' : '140px'
                  }}
                >
                  <div style={styles.podiumRank}>
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                  </div>
                  <div style={styles.podiumPlayer}>
                    {player.teamLogo && (
                      <img 
                        src={getLogoUrl(player.teamLogo)}
                        alt=""
                        style={styles.podiumLogo}
                      />
                    )}
                    <div style={styles.podiumName}>{player.name[language]}</div>
                    <div style={styles.podiumTeam}>{player.team}</div>
                    <div style={styles.podiumGoals}>
                      <FaFutbol style={styles.podiumIcon} />
                      {player.goals}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t.rank}</th>
                    <th style={{...styles.th, textAlign: language === 'ar' ? 'right' : 'left'}}>{t.player}</th>
                    <th style={styles.th}>{t.team}</th>
                    <th style={styles.th}>{t.goals}</th>
                    <th style={styles.th}>{t.matches}</th>
                    <th style={styles.th}>{t.penalties}</th>
                  </tr>
                </thead>
                <tbody>
                  {scorers.map((player, index) => (
                    <tr key={player.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.rank}>{index + 1}</span>
                      </td>
                      <td style={{...styles.td, textAlign: language === 'ar' ? 'right' : 'left'}}>
                        <div style={styles.playerCell}>
                          <span style={styles.playerName}>{player.name[language]}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.teamCell}>
                          {player.teamLogo && (
                            <img 
                              src={getLogoUrl(player.teamLogo)}
                              alt=""
                              style={styles.miniLogo}
                            />
                          )}
                          <span style={styles.teamName}>{player.team}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.goalsValue}>{player.goals}</span>
                      </td>
                      <td style={styles.td}>{player.matches}</td>
                      <td style={styles.td}>{player.penalties}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Yellow Cards */}
        {activeTab === 'yellow' && (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t.rank}</th>
                  <th style={{...styles.th, textAlign: language === 'ar' ? 'right' : 'left'}}>{t.player}</th>
                  <th style={styles.th}>{t.team}</th>
                  <th style={styles.th}>{t.yellow}</th>
                  <th style={styles.th}>{t.red}</th>
                  <th style={styles.th}>{t.matches}</th>
                </tr>
              </thead>
              <tbody>
                {yellowCards.map((player, index) => (
                  <tr key={player.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.rank}>{index + 1}</span>
                    </td>
                    <td style={{...styles.td, textAlign: language === 'ar' ? 'right' : 'left'}}>
                      <span style={styles.playerName}>{player.name[language]}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.teamCell}>
                        {player.teamLogo && (
                          <img 
                            src={getLogoUrl(player.teamLogo)}
                            alt=""
                            style={styles.miniLogo}
                          />
                        )}
                        <span style={styles.teamName}>{player.team}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.yellowCard}>
                        <FaSquare style={{color: '#FFC107', fontSize: '14px'}} /> {player.yellowCards}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.redCard}>
                        <FaSquare style={{color: '#f44336', fontSize: '14px'}} /> {player.redCards}
                      </span>
                    </td>
                    <td style={styles.td}>{player.matches}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Red Cards */}
        {activeTab === 'red' && (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t.rank}</th>
                  <th style={{...styles.th, textAlign: language === 'ar' ? 'right' : 'left'}}>{t.player}</th>
                  <th style={styles.th}>{t.team}</th>
                  <th style={styles.th}>{t.red}</th>
                  <th style={styles.th}>{t.yellow}</th>
                  <th style={styles.th}>{t.matches}</th>
                </tr>
              </thead>
              <tbody>
                {redCards.map((player, index) => (
                  <tr key={player.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.rank}>{index + 1}</span>
                    </td>
                    <td style={{...styles.td, textAlign: language === 'ar' ? 'right' : 'left'}}>
                      <span style={styles.playerName}>{player.name[language]}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.teamCell}>
                        {player.teamLogo && (
                          <img 
                            src={getLogoUrl(player.teamLogo)}
                            alt=""
                            style={styles.miniLogo}
                          />
                        )}
                        <span style={styles.teamName}>{player.team}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.redCard}>
                        <FaSquare style={{color: '#f44336', fontSize: '14px'}} /> {player.redCards}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.yellowCard}>
                        <FaSquare style={{color: '#FFC107', fontSize: '14px'}} /> {player.yellowCards}
                      </span>
                    </td>
                    <td style={styles.td}>{player.matches}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Team Stats */}
        {activeTab === 'teams' && (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t.rank}</th>
                  <th style={{...styles.th, textAlign: language === 'ar' ? 'right' : 'left'}}>{t.team}</th>
                  <th style={styles.th}>{t.matches}</th>
                  <th style={styles.th}>{t.goalsScored}</th>
                  <th style={styles.th}>{t.goalsConceded}</th>
                  <th style={styles.th}>{t.goalDiff}</th>
                  <th style={styles.th}>{t.average}</th>
                </tr>
              </thead>
              <tbody>
                {teamStats.map((team, index) => (
                  <tr key={team.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.rank}>{index + 1}</span>
                    </td>
                    <td style={{...styles.td, textAlign: language === 'ar' ? 'right' : 'left'}}>
                      <div style={styles.teamCell}>
                        {team.logo && (
                          <img 
                            src={getLogoUrl(team.logo)}
                            alt=""
                            style={styles.miniLogo}
                          />
                        )}
                        <span style={styles.teamName}>{team.name}</span>
                      </div>
                    </td>
                    <td style={styles.td}>{team.matches}</td>
                    <td style={styles.td}>
                      <span style={{...styles.goalsValue, color: '#4CAF50'}}>{team.goalsScored}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{color: '#f44336'}}>{team.goalsConceded}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        color: team.goalDifference > 0 ? '#4CAF50' : team.goalDifference < 0 ? '#f44336' : '#666',
                        fontWeight: 'bold'
                      }}>
                        {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                      </span>
                    </td>
                    <td style={styles.td}>{team.avgScored}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  tabsSection: {
    background: 'white',
    borderBottom: '2px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  tabs: {
    display: 'flex',
    maxWidth: '1200px',
    margin: '0 auto',
    gap: '10px',
    padding: '0 20px',
    flexWrap: 'wrap'
  },
  tab: {
    flex: 1,
    minWidth: '200px',
    padding: '15px 20px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s'
  },
  activeTab: {
    color: '#1a365d',
    borderBottomColor: '#1a365d'
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  statsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  },
  podium: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    gap: '20px',
    marginBottom: '40px',
    padding: '40px 20px'
  },
  podiumItem: {
    background: 'white',
    borderRadius: '15px 15px 0 0',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: '200px',
    boxShadow: '0 -4px 15px rgba(0,0,0,0.1)',
    transition: 'all 0.3s'
  },
  podiumRank: {
    fontSize: '48px',
    marginBottom: '15px'
  },
  podiumPlayer: {
    textAlign: 'center'
  },
  podiumLogo: {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
    marginBottom: '10px'
  },
  podiumName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '5px'
  },
  podiumTeam: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '10px'
  },
  podiumGoals: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1a365d',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  podiumIcon: {
    fontSize: '24px'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '15px',
    padding: '30px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '15px',
    background: '#1a365d',
    color: 'white',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'all 0.3s'
  },
  td: {
    padding: '15px',
    textAlign: 'center',
    fontSize: '14px'
  },
  rank: {
    display: 'inline-block',
    width: '30px',
    height: '30px',
    background: '#f5f5f5',
    borderRadius: '50%',
    lineHeight: '30px',
    fontWeight: 'bold',
    color: '#333'
  },
  playerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  playerName: {
    fontWeight: '600',
    color: '#333'
  },
  teamCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  miniLogo: {
    width: '25px',
    height: '25px',
    objectFit: 'contain'
  },
  teamName: {
    fontSize: '13px',
    color: '#666'
  },
  goalsValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1a365d'
  },
  yellowCard: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontWeight: 'bold'
  },
  redCard: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontWeight: 'bold'
  }
};

export default StatisticsPage;
